import { useState } from 'react'
import { Label } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Card, CardContent, CardHeader, CardTitle } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Plus, X, Settings, FileText } from 'lucide-react'
import type { ScenarioInput, SavedSystem, SavedFormat } from '@creanizer/types'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { useItemStore } from '../../../../stores/itemStore'
import { useEnemyStore } from '../../../../stores/enemyStore'
import { useRelationshipStore } from '../../../../stores/relationshipStore'
import { usePlotStore } from '../../../../stores/plotStore'
import { useTitleStore } from '../../../../stores/titleStore'
import { useDocumentStore } from '../../../../stores/documentStore'
import { useSkillStore } from '../../../../stores/skillStore'
import { useSystemSkillStore } from '../../../../stores/systemSkillStore'
import { GeneratorDataSelector } from './GeneratorDataSelector'
import { ReferenceMaterialsCard } from './ReferenceMaterialsCard'

interface ScenarioGenerateModeProps {
  input: ScenarioInput
  setInput: (input: ScenarioInput) => void
  systems: SavedSystem[]
  formats: SavedFormat[]
}

export function ScenarioGenerateModeSimple({ input, setInput, systems = [], formats = [] }: ScenarioGenerateModeProps) {
  const settings = input?.generateSettings || {}
  const [showCustomSystem, setShowCustomSystem] = useState(false)
  const [showCustomFormat, setShowCustomFormat] = useState(false)
  const [showCustomGenre, setShowCustomGenre] = useState(settings.genre === 'custom')
  
  // 各ストアから保存済みデータを取得（安全にアクセス）
  const worldStore = useWorldBuilderStore()
  const savedWorlds = worldStore?.savedWorlds || []
  const currentWorld = worldStore?.currentWorld || null
  
  const charStore = useCharacterStore()
  const characters = charStore?.characters || []
  
  const itemStore = useItemStore()
  const items = itemStore?.items || []
  
  const enemyStore = useEnemyStore()
  const enemies = enemyStore?.enemies || []
  
  const relationshipStore = useRelationshipStore()
  const relationships = relationshipStore?.savedRelationships || []
  
  const plotStore = usePlotStore()
  const plots = plotStore?.savedPlots || []
  
  const titleStore = useTitleStore()
  const titles = titleStore?.savedTitles || []
  
  const documentStore = useDocumentStore()
  const documents = documentStore?.documents || []

  const skillStore = useSkillStore()
  const skills = skillStore?.skills || []

  const systemSkillStore = useSystemSkillStore()
  const systemSkillDatabases = systemSkillStore?.databases || []
  // スキルDBからフラットなスキルリストを作成（システム名を含む）
  const systemSkills = systemSkillDatabases.flatMap(db =>
    db.skills.map(skill => ({
      ...skill,
      id: skill.id,
      name: `${skill.name} (${db.systemName})`,
      originalName: skill.name,
      systemName: db.systemName,
      systemId: db.id
    }))
  )

  const updateSettings = (updates: Partial<ScenarioInput['generateSettings']>) => {
    setInput({
      ...(input || { mode: 'generate' as const }),
      generateSettings: {
        ...settings,
        ...updates
      }
    })
  }
  
  const handleSubObjectiveAdd = () => {
    const current = settings.subObjectives || []
    updateSettings({ subObjectives: [...current, ''] })
  }
  
  const handleSubObjectiveUpdate = (index: number, value: string) => {
    const current = settings.subObjectives || []
    const updated = [...current]
    updated[index] = value
    updateSettings({ subObjectives: updated })
  }
  
  const handleSubObjectiveRemove = (index: number) => {
    const current = settings.subObjectives || []
    updateSettings({ subObjectives: current.filter((_, i) => i !== index) })
  }
  
  return (
    <div className="space-y-6">
      {/* 基本設定 */}
      <Card>
        <CardHeader>
          <CardTitle>基本設定</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="title">シナリオタイトル（オプション）</Label>
            <Input
              id="title"
              value={settings.title || ''}
              onChange={(e) => updateSettings({ title: e.target.value })}
              placeholder="自動生成される場合は空欄でOK"
              className="mt-2"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="genre">ジャンル</Label>
              <Select 
                value={settings.genre || 'fantasy'} 
                onValueChange={(value) => {
                  updateSettings({ genre: value as any })
                  setShowCustomGenre(value === 'custom')
                }}
              >
                <SelectTrigger id="genre" className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="fantasy">ファンタジー</SelectItem>
                  <SelectItem value="sf">SF</SelectItem>
                  <SelectItem value="modern">現代</SelectItem>
                  <SelectItem value="horror">ホラー</SelectItem>
                  <SelectItem value="mystery">ミステリー</SelectItem>
                  <SelectItem value="historical">歴史</SelectItem>
                  <SelectItem value="custom">カスタム</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {showCustomGenre && (
              <div>
                <Label htmlFor="custom-genre">カスタムジャンル</Label>
                <Input
                  id="custom-genre"
                  value={settings.customGenre || ''}
                  onChange={(e) => updateSettings({ customGenre: e.target.value })}
                  placeholder="ジャンル名を入力"
                  className="mt-2"
                />
              </div>
            )}
            
            <div>
              <Label htmlFor="difficulty">難易度</Label>
              <Select 
                value={settings.difficulty || 'intermediate'} 
                onValueChange={(value) => updateSettings({ difficulty: value as any })}
              >
                <SelectTrigger id="difficulty" className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="beginner">初心者向け</SelectItem>
                  <SelectItem value="intermediate">中級者向け</SelectItem>
                  <SelectItem value="advanced">上級者向け</SelectItem>
                  <SelectItem value="expert">エキスパート</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="playTime">プレイ時間</Label>
              <Select 
                value={settings.playTime || '2-3h'} 
                onValueChange={(value) => updateSettings({ playTime: value as any })}
              >
                <SelectTrigger id="playTime" className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1h">1時間</SelectItem>
                  <SelectItem value="2-3h">2-3時間</SelectItem>
                  <SelectItem value="4-6h">4-6時間</SelectItem>
                  <SelectItem value="8h+">8時間以上</SelectItem>
                  <SelectItem value="campaign">キャンペーン</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="playerCount">プレイ人数</Label>
              <Input
                id="playerCount"
                value={settings.playerCount || '3-5人'}
                onChange={(e) => updateSettings({ playerCount: e.target.value })}
                placeholder="例: 3-5人"
                className="mt-2"
              />
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* 詳細設定 */}
      <Card>
        <CardHeader>
          <CardTitle>詳細設定</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="theme">テーマ</Label>
            <Input
              id="theme"
              value={settings.theme || ''}
              onChange={(e) => updateSettings({ theme: e.target.value })}
              placeholder="例: 友情、裏切り、成長"
              className="mt-2"
            />
          </div>
          
          <div>
            <Label htmlFor="atmosphere">雰囲気</Label>
            <Input
              id="atmosphere"
              value={settings.atmosphere || ''}
              onChange={(e) => updateSettings({ atmosphere: e.target.value })}
              placeholder="例: ダーク、コミカル、シリアス"
              className="mt-2"
            />
          </div>
          
          <div>
            <Label htmlFor="mainObjective">主目的</Label>
            <Input
              id="mainObjective"
              value={settings.mainObjective || ''}
              onChange={(e) => updateSettings({ mainObjective: e.target.value })}
              placeholder="例: 古代の遺跡を探索し、失われた秘宝を見つける"
              className="mt-2"
            />
          </div>
          
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>副目的（オプション）</Label>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={handleSubObjectiveAdd}
              >
                <Plus className="w-4 h-4 mr-1" />
                追加
              </Button>
            </div>
            <div className="space-y-2">
              {(settings.subObjectives || []).map((objective, index) => (
                <div key={index} className="flex gap-2">
                  <Input
                    value={objective}
                    onChange={(e) => handleSubObjectiveUpdate(index, e.target.value)}
                    placeholder="副目的を入力"
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleSubObjectiveRemove(index)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* システムとフォーマット */}
      <Card>
        <CardHeader>
          <CardTitle>システムとフォーマット</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="system">適用システム</Label>
            <Select 
              value={settings.systemId || 'none'} 
              onValueChange={(value) => {
                updateSettings({ systemId: value })
                setShowCustomSystem(value === 'custom')
              }}
            >
              <SelectTrigger id="system" className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">指定なし（汎用）</SelectItem>
                <SelectItem value="custom">カスタム入力</SelectItem>
                {(systems || []).map((system) => (
                  <SelectItem key={system.id} value={system.id}>
                    <div className="flex items-center gap-2">
                      <Settings className="w-4 h-4" />
                      {system.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {showCustomSystem && (
              <Textarea
                value={settings.customSystem || ''}
                onChange={(e) => updateSettings({ customSystem: e.target.value })}
                placeholder="システムの説明を入力（判定方法、特殊ルールなど）"
                rows={4}
                className="mt-2"
              />
            )}
          </div>
          
          <div>
            <Label htmlFor="format">適用フォーマット</Label>
            <Select 
              value={settings.formatId || 'none'} 
              onValueChange={(value) => {
                updateSettings({ formatId: value })
                setShowCustomFormat(value === 'custom')
              }}
            >
              <SelectTrigger id="format" className="mt-2">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">指定なし（自動）</SelectItem>
                <SelectItem value="custom">カスタム入力</SelectItem>
                {(formats || []).map((format) => (
                  <SelectItem key={format.id} value={format.id}>
                    <div className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      {format.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {showCustomFormat && (
              <Textarea
                value={settings.customFormat || ''}
                onChange={(e) => updateSettings({ customFormat: e.target.value })}
                placeholder="フォーマットの説明を入力（構成、スタイルなど）"
                rows={4}
                className="mt-2"
              />
            )}
          </div>
        </CardContent>
      </Card>
      
      {/* ジェネレータ連携 */}
      <Card>
        <CardHeader>
          <CardTitle>他ジェネレータ連携</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="world">
            <TabsList className="grid grid-cols-2 gap-2 h-auto mb-4">
              <TabsTrigger value="world" className="data-[state=active]:bg-blue-50">世界観</TabsTrigger>
              <TabsTrigger value="character" className="data-[state=active]:bg-blue-50">キャラクター</TabsTrigger>
              <TabsTrigger value="item" className="data-[state=active]:bg-blue-50">アイテム</TabsTrigger>
              <TabsTrigger value="enemy" className="data-[state=active]:bg-blue-50">エネミー</TabsTrigger>
              <TabsTrigger value="skill" className="data-[state=active]:bg-blue-50">スキル</TabsTrigger>
              <TabsTrigger value="system-skill" className="data-[state=active]:bg-blue-50">スキルDB</TabsTrigger>
              <TabsTrigger value="relationship" className="data-[state=active]:bg-blue-50">関係性</TabsTrigger>
              <TabsTrigger value="plot" className="data-[state=active]:bg-blue-50">プロット</TabsTrigger>
              <TabsTrigger value="title" className="data-[state=active]:bg-blue-50">タイトル</TabsTrigger>
              <TabsTrigger value="document" className="data-[state=active]:bg-blue-50">文献</TabsTrigger>
            </TabsList>
            
            <TabsContent value="world">
              <GeneratorDataSelector
                type="world"
                label="使用する世界観"
                data={savedWorlds}
                selectedId={settings.worldContext}
                onSelect={(id, world) => {
                  updateSettings({ 
                    worldContext: id === 'none' ? undefined : id
                  })
                }}
              />
            </TabsContent>
            
            <TabsContent value="character">
              <GeneratorDataSelector
                type="character"
                label="登場キャラクター"
                data={characters}
                selectedIds={settings.characterData?.map(cd => cd.id) || []}
                multiSelect={true}
                onMultiSelect={(selected) => {
                  updateSettings({
                    characterData: selected.map(c => ({
                      id: c.id,
                      role: 'npc' as const,
                      data: c
                    }))
                  })
                }}
                roleOptions={['pc', 'npc', 'boss', 'enemy', 'ally']}
              />
            </TabsContent>
            
            <TabsContent value="item">
              <GeneratorDataSelector
                type="item"
                label="登場アイテム"
                data={items}
                selectedIds={settings.itemData?.map(id => id.id) || []}
                multiSelect={true}
                onMultiSelect={(selected) => {
                  updateSettings({
                    itemData: selected.map(i => ({
                      id: i.id,
                      role: 'key_item' as const,
                      data: i
                    }))
                  })
                }}
                roleOptions={['key_item', 'reward', 'plot_device', 'optional']}
              />
            </TabsContent>
            
            <TabsContent value="enemy">
              <GeneratorDataSelector
                type="enemy"
                label="登場エネミー"
                data={enemies}
                selectedIds={settings.enemyData?.map(ed => ed.id) || []}
                multiSelect={true}
                onMultiSelect={(selected) => {
                  updateSettings({
                    enemyData: selected.map(e => ({
                      id: e.id,
                      role: 'minion' as const,
                      data: e
                    }))
                  })
                }}
                roleOptions={['minion', 'boss', 'encounter', 'guardian']}
              />
            </TabsContent>

            <TabsContent value="skill">
              <GeneratorDataSelector
                type="skill"
                label="登場スキル"
                data={skills}
                selectedIds={settings.skillData?.map(sd => sd.id) || []}
                multiSelect={true}
                onMultiSelect={(selected) => {
                  updateSettings({
                    skillData: selected.map(s => ({
                      id: s.id,
                      role: 'enemy_skill' as const,
                      data: s
                    }))
                  })
                }}
                roleOptions={['enemy_skill', 'pc_skill', 'npc_skill', 'environmental', 'plot_device']}
              />
            </TabsContent>

            <TabsContent value="system-skill">
              <GeneratorDataSelector
                type="system-skill"
                label="システム定義スキル"
                data={systemSkills}
                selectedIds={settings.systemSkillData?.map(sd => sd.id) || []}
                multiSelect={true}
                onMultiSelect={(selected) => {
                  updateSettings({
                    systemSkillData: selected.map(s => ({
                      id: s.id,
                      systemId: (s as any).systemId,
                      data: s
                    }))
                  })
                }}
              />
              {systemSkillDatabases.length === 0 && (
                <p className="text-sm text-gray-500 mt-2">
                  スキルDBタブでシステムのスキルを登録してください
                </p>
              )}
            </TabsContent>

            <TabsContent value="relationship">
              <GeneratorDataSelector
                type="relationship"
                label="関係性設定"
                data={relationships}
                selectedIds={settings.relationshipData?.map(rd => rd.id) || []}
                multiSelect={true}
                onMultiSelect={(selected) => {
                  updateSettings({
                    relationshipData: selected.map(r => ({
                      id: r.id,
                      relevance: 'central' as const,
                      data: r
                    }))
                  })
                }}
                roleOptions={['central', 'subplot', 'background']}
              />
            </TabsContent>
            
            <TabsContent value="plot">
              <GeneratorDataSelector
                type="plot"
                label="プロット参考"
                data={plots}
                selectedIds={settings.plotData?.map(pd => pd.id) || []}
                multiSelect={true}
                onMultiSelect={(selected) => {
                  updateSettings({
                    plotData: selected.map(p => ({
                      id: p.id,
                      usage: 'main' as const,
                      data: p
                    }))
                  })
                }}
                roleOptions={['main', 'backstory', 'hook']}
              />
            </TabsContent>
            
            <TabsContent value="title">
              <GeneratorDataSelector
                type="title"
                label="タイトル参考"
                data={titles}
                selectedIds={settings.titleData?.map(td => td.id) || []}
                multiSelect={true}
                onMultiSelect={(selected) => {
                  updateSettings({
                    titleData: selected.map(t => ({
                      id: t.id,
                      usage: 'scenario' as const,
                      data: t
                    }))
                  })
                }}
                roleOptions={['scenario', 'chapter', 'location', 'item']}
              />
            </TabsContent>
            
            <TabsContent value="document">
              <GeneratorDataSelector
                type="document"
                label="文献参考"
                data={documents}
                selectedIds={settings.literatureData?.map(ld => ld.id) || []}
                multiSelect={true}
                onMultiSelect={(selected) => {
                  updateSettings({
                    literatureData: selected.map(d => ({
                      id: d.id,
                      usage: 'reference' as const,
                      data: d
                    }))
                  })
                }}
                roleOptions={['reference', 'handout', 'prophecy', 'document']}
              />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
      
      {/* 生成オプション */}
      <Card>
        <CardHeader>
          <CardTitle>生成オプション</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>創造性レベル: {settings.creativityLevel || 7}</Label>
            <Slider
              value={[settings.creativityLevel || 7]}
              onValueChange={([value]) => updateSettings({ creativityLevel: value })}
              min={1}
              max={10}
              step={1}
              className="mt-2"
            />
            <div className="flex justify-between text-xs text-gray-500">
              <span>保守的</span>
              <span>バランス</span>
              <span>創造的</span>
            </div>
          </div>
          
          <div className="space-y-2">
            <Checkbox
              id="includeGMNotes"
              checked={settings.includeGMNotes || false}
              onCheckedChange={(checked) => updateSettings({ includeGMNotes: !!checked })}
            />
            <Label htmlFor="includeGMNotes" className="ml-2">GM向けメモを含める</Label>
          </div>
          
          <div className="space-y-2">
            <Checkbox
              id="includeMaps"
              checked={settings.includeMaps || false}
              onCheckedChange={(checked) => updateSettings({ includeMaps: !!checked })}
            />
            <Label htmlFor="includeMaps" className="ml-2">マップ説明を含める</Label>
          </div>
          
          <div className="space-y-2">
            <Checkbox
              id="includeHandouts"
              checked={settings.includeHandouts || false}
              onCheckedChange={(checked) => updateSettings({ includeHandouts: !!checked })}
            />
            <Label htmlFor="includeHandouts" className="ml-2">ハンドアウトを含める</Label>
          </div>
          
          <div className="space-y-2">
            <Checkbox
              id="useProMode"
              checked={settings.useProMode || false}
              onCheckedChange={(checked) => updateSettings({
                useProMode: !!checked,
                useGemini3Mode: checked ? false : settings.useGemini3Mode,
                useGemini3FlashMode: checked ? false : settings.useGemini3FlashMode
              })}
            />
            <Label htmlFor="useProMode" className="ml-2">
              Proモード（高品質・低速）
            </Label>
          </div>

          <div className="space-y-2">
            <Checkbox
              id="useGemini3Mode"
              checked={settings.useGemini3Mode || false}
              onCheckedChange={(checked) => updateSettings({
                useGemini3Mode: !!checked,
                useProMode: checked ? false : settings.useProMode,
                useGemini3FlashMode: checked ? false : settings.useGemini3FlashMode
              })}
            />
            <Label htmlFor="useGemini3Mode" className="ml-2">
              Gemini 3 モード（有料APIのみ・試験版）
            </Label>
          </div>

          <div className="space-y-2">
            <Checkbox
              id="useGemini3FlashMode"
              checked={settings.useGemini3FlashMode || false}
              onCheckedChange={(checked) => updateSettings({
                useGemini3FlashMode: !!checked,
                useProMode: checked ? false : settings.useProMode,
                useGemini3Mode: checked ? false : settings.useGemini3Mode
              })}
            />
            <Label htmlFor="useGemini3FlashMode" className="ml-2">
              Gemini 3 Flash モード（有料APIのみ・試験版・高速）
            </Label>
          </div>
        </CardContent>
      </Card>

      {/* 参考資料 */}
      <ReferenceMaterialsCard
        materials={settings.referenceMaterials || []}
        onChange={(materials) => updateSettings({ referenceMaterials: materials })}
      />

      {/* 自由入力欄 */}
      <Card>
        <CardHeader>
          <CardTitle>追加指示（自由入力）</CardTitle>
        </CardHeader>
        <CardContent>
          <div>
            <Label htmlFor="additionalSettings">追加の要望や特殊な設定</Label>
            <Textarea
              id="additionalSettings"
              value={settings.additionalSettings || ''}
              onChange={(e) => updateSettings({ additionalSettings: e.target.value })}
              placeholder="例：
・主人公は記憶喪失の設定にしてください
・最後は必ずハッピーエンドにしてください  
・戦闘は少なめで、謎解きを中心にしてください
・NPCの一人は裏切り者にしてください
・舞台は中世風の港町にしてください"
              rows={6}
              className="mt-2"
            />
            <p className="text-xs text-gray-500 mt-2">
              シナリオ生成に関する特殊な要望や、詳細な設定を自由に記述できます。
              ここに書いた内容は、AIがシナリオを生成する際に考慮されます。
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}